import { CandidateType } from '../../../../mirage/types';
import { CandidateItem } from './CandidateItem';
import { Select, SelectItem } from '@tremor/react';
import { useMemo, useState } from 'react';

// Define filter option types (adjust values as needed)
const DATE_SORT_OPTIONS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
};
// Add "All" options and map values to potential CandidateType properties
// You might want to generate these dynamically based on available candidate data
const CATEGORY_OPTIONS = ['All', 'Research & Development', 'Marketing', 'Sales']; // Assuming 'department' field
const CITY_OPTIONS = ['All', 'San Francisco, CA', 'New York, NY', 'London']; // Assuming 'location' field

export const CandidateList = ({
  candidates,
  showUser,
}: {
  candidates: CandidateType[];
  showUser: (candidate: CandidateType) => void;
}) => {
  // State for filters and sorting
  const [selectedDateSort, setSelectedDateSort] = useState<string>(DATE_SORT_OPTIONS.NEWEST);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedCity, setSelectedCity] = useState<string>('All');

  // Apply filters and sorting
  const displayCandidates = useMemo(() => {
    let filtered = [...candidates]; // Start with the candidates passed in

    // Apply category filter
    if (selectedCategory !== 'All') {
      // Assuming 'department' field matches category options
      filtered = filtered.filter(c => c.department === selectedCategory);
    }

    // Apply city filter
    if (selectedCity !== 'All') {
      // Assuming 'location' field matches city options
      filtered = filtered.filter(c => c.location === selectedCity);
    }

    // Apply date sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime(); // Ensure date is comparable
      const dateB = new Date(b.date).getTime();
      return selectedDateSort === DATE_SORT_OPTIONS.NEWEST ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [candidates, selectedDateSort, selectedCategory, selectedCity]);

  // Get the total count before filtering (for display purposes)
  const totalCandidatesBeforeFiltering = candidates.length;

  return (
    <div className="flex w-full flex-col gap-3 px-6 pb-6">
      {/* Dynamically display counts */}
      <p className="text-sm text-gray-600">
        Showing {displayCandidates.length} results out of {totalCandidatesBeforeFiltering} candidates
        {totalCandidatesBeforeFiltering !== candidates.length ? ' (after search)' : ''}
      </p>
      <div className="flex flex-wrap justify-between gap-y-2">
        {/* Date Sorting Select */}
        <Select
          className="w-full sm:w-[48%] md:w-[32%]"
          value={selectedDateSort}
          onValueChange={setSelectedDateSort}
        >
          <SelectItem value={DATE_SORT_OPTIONS.NEWEST}>Newest First</SelectItem>
          <SelectItem value={DATE_SORT_OPTIONS.OLDEST}>Oldest First</SelectItem>
        </Select>
        {/* Category Filter Select */}
        <Select
          className="w-full sm:w-[48%] md:w-[32%]"
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          {CATEGORY_OPTIONS.map(option => (
            <SelectItem key={option} value={option}>{option === 'All' ? 'All Categories' : option}</SelectItem>
          ))}
        </Select>
        {/* City Filter Select */}
        <Select
          className="w-full sm:w-[48%] md:w-[32%]"
          value={selectedCity}
          onValueChange={setSelectedCity}
        >
          {CITY_OPTIONS.map(option => (
            <SelectItem key={option} value={option}>{option === 'All' ? 'All Cities' : option}</SelectItem>
          ))}
        </Select>
      </div>

      {/* Render the filtered and sorted list */}
      {displayCandidates.length > 0 ? (
        displayCandidates.map((candidate, index) => (
          <CandidateItem
            showUser={showUser}
            key={candidate.id || index}
            candidate={candidate}
          />
        ))
      ) : (
        // Show a message if filters result in no candidates
        <div className="py-6 text-center text-gray-500">
          No candidates match the current filters.
        </div>
      )}
    </div>
  );
};
